import { useListState } from "../../../lib/listState";
import { useEffect, useMemo, useRef, useState, type DragEvent, type PointerEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRightLeft, Check, ChevronDown, ChevronRight, Crown, GitFork, GripVertical, Loader2, Maximize2, Minus, Plus, RefreshCw, Search, Undo2, UserMinus, UserPlus, Users, X } from "lucide-react";
import SearchableSelect from "../../../components/SearchableSelect";
import { api, ApiError } from "../../../lib/api";
import type { StaffReportingUser, StaffReportingView } from "./reportingTypes";
import { CARD_HEIGHT, CARD_WIDTH, branchIds, canAssign, childrenByManager, reportingLayout, type ChildSlot } from "./reportingTree";
import "./staffReporting.css";

function selectOptionMonogram(name: string) { return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase(); }

const TRANSFER_TYPE = "application/x-coprint-staff-membership";
const EMPTY_SLOTS: ChildSlot[] = [];

function displayName(user: StaffReportingUser) {
  return user.name || user.mobile_number || "Staff member";
}

function Avatar({ user, owner = false }: { user: StaffReportingUser; owner?: boolean }) {
  const tone = owner ? "owner" : user.role?.code.includes("MANAGER") ? "manager" : "staff";
  return <span className={`reporting-avatar ${tone}`} aria-hidden="true">{selectOptionMonogram(displayName(user))}</span>;
}

type Change = { userId: string; managerId: string | null; slotId?: string; undo?: boolean };
type Undo = { userId: string; managerId: string | null; name: string };

export default function OrganizationReporting({ organizationId, active }: { organizationId: string; active: boolean }) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["organization-reporting", organizationId], [organizationId]);
  const reportingQuery = useQuery({ queryKey, queryFn: () => api.getOrganizationReporting(organizationId), enabled: active });
  const data = reportingQuery.data;
  const [slots, setSlots] = useState<ChildSlot[]>(EMPTY_SLOTS);
  const [childOrder, setChildOrder] = useState(new Map<string, string[]>());
  const [collapsed, setCollapsed] = useState(new Set<string>());
  const [search, setSearch] = useListState("reporting.search", "");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overSlot, setOverSlot] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [zoom, setZoom] = useState(1);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [panning, setPanning] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const centeredRef = useRef(false);
  const removeDialogRef = useRef<HTMLElement>(null);
  const pendingRef = useRef(false);
  const panRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const layout = useMemo(() => data ? reportingLayout(data, slots, collapsed, size.width || 640, childOrder) : null,
    [data, slots, collapsed, size.width, childOrder]);
  const usersById = useMemo(() => new Map(data?.users.map((user) => [user.organization_user_id, user]) ?? []), [data]);
  const children = useMemo(() => childrenByManager(data?.users ?? [], data?.root_organization_user_id), [data]);
  const outside = useMemo(() => data?.users.filter((user) => !layout?.connected.has(user.organization_user_id))
    .sort((first, second) => displayName(first).localeCompare(displayName(second))) ?? [], [data, layout?.connected]);
  const visibleOutside = useMemo(() => {
    const value = search.trim().toLowerCase();
    return outside.filter((user) => `${user.name} ${user.email} ${user.mobile_number} ${user.role?.name ?? ""}`.toLowerCase().includes(value));
  }, [outside, search]);
  const selected = selectedId ? usersById.get(selectedId) : null;
  const removal = removeId ? usersById.get(removeId) : null;

  const change = useMutation({
    mutationFn: (operation: Change) => operation.managerId
      ? api.setOrganizationReportingManager(organizationId, operation.userId, operation.managerId)
      : api.clearOrganizationReportingManager(organizationId, operation.userId),
    onSuccess: (next, operation) => {
      const previous = usersById.get(operation.userId);
      queryClient.setQueryData<StaffReportingView>(queryKey, next);
      const connected = branchIds(next.users, next.root_organization_user_id);
      setSlots((current) => current.filter((slot) => slot.id !== operation.slotId && connected.has(slot.parentId)));
      setChildOrder((current) => {
        const order = new Map(current);
        for (const [parent, ids] of order) order.set(parent, ids.filter((id) => id !== operation.userId));
        if (operation.slotId && operation.managerId) {
          order.set(operation.managerId, (order.get(operation.managerId) ?? layout?.childIds.get(operation.managerId) ?? [])
            .map((id) => id === operation.slotId ? operation.userId : id));
        }
        return order;
      });
      setUndo(operation.undo || !previous ? null : {
        userId: operation.userId, managerId: previous.reports_to_organization_user_id, name: displayName(previous)
      });
      setSelectedId(null);
      setRemoveId(null);
      setOverSlot(null);
      setFocusId(operation.managerId ? operation.userId : next.root_organization_user_id);
      setAnnouncement(operation.undo ? "Reporting change undone." : `${previous ? displayName(previous) : "Staff"}'s reporting updated.`);
      void queryClient.invalidateQueries({ queryKey: ["tech-ops-users"] });
      void queryClient.invalidateQueries({ queryKey: ["tech-ops-user"] });
      void queryClient.invalidateQueries({ queryKey: ["tech-admin-audit"] });
      void queryClient.invalidateQueries({ queryKey: ["organization-location-pocs", organizationId] });
    }
  });
  const editable = Boolean(data?.can_manage) && !change.isPending && !reportingQuery.isFetching;
  pendingRef.current = change.isPending;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [Boolean(data)]);

  useEffect(() => {
    if (!layout || !size.width || !viewportRef.current) return;
    const targetId = focusId ?? (!centeredRef.current ? data?.root_organization_user_id : null);
    const target = layout.nodes.find((node) => node.id === targetId);
    if (!target) return;
    const viewport = viewportRef.current;
    let left = viewport.scrollLeft;
    let top = viewport.scrollTop;
    if (!centeredRef.current) {
      left = Math.max(0, (target.x + CARD_WIDTH / 2) * zoom - size.width / 2);
      top = 0;
    } else {
      if (target.x * zoom < left + 24) left = Math.max(0, target.x * zoom - 24);
      else if ((target.x + CARD_WIDTH) * zoom > left + size.width - 24) left = (target.x + CARD_WIDTH) * zoom - size.width + 24;
      if (target.y * zoom < top + 24) top = Math.max(0, target.y * zoom - 24);
      else if ((target.y + CARD_HEIGHT) * zoom > top + size.height - 24) top = (target.y + CARD_HEIGHT) * zoom - size.height + 24;
    }
    centeredRef.current = true;
    viewport.scrollTo({ left, top, behavior: "instant" });
    if (focusId) setFocusId(null);
  }, [layout, size.width, size.height, zoom, focusId, data?.root_organization_user_id]);

  useEffect(() => {
    if (!removeId) return;
    const previous = document.activeElement as HTMLElement | null;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pendingRef.current) { event.preventDefault(); setRemoveId(null); }
      if (event.key !== "Tab") return;
      const buttons = [...(removeDialogRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); if (previous?.isConnected) previous.focus(); else viewportRef.current?.focus(); };
  }, [removeId]);

  function addChild(parentId: string) {
    if (!editable) return;
    const slot = { id: `slot-${crypto.randomUUID()}`, parentId };
    setSlots((current) => [...current, slot]);
    setChildOrder((current) => new Map(current).set(parentId, [...(layout?.childIds.get(parentId) ?? []), slot.id]));
    setCollapsed((current) => { const next = new Set(current); next.delete(parentId); return next; });
    setFocusId(slot.id);
    setAnnouncement(`New reportee slot added below ${displayName(usersById.get(parentId)!)}.`);
  }

  function assign(userId: string, slot: ChildSlot) {
    if (!editable || !data) return;
    if (!canAssign(data.users, data.root_organization_user_id, userId, slot.parentId)) {
      setAnnouncement("A staff member cannot report to themselves or to one of their reportees.");
      return;
    }
    change.mutate({ userId, managerId: slot.parentId, slotId: slot.id });
  }

  function dragStart(event: DragEvent, user: StaffReportingUser) {
    if (!editable || user.organization_user_id === data?.root_organization_user_id) { event.preventDefault(); return; }
    event.dataTransfer.setData(TRANSFER_TYPE, user.organization_user_id);
    event.dataTransfer.effectAllowed = "move";
    setDraggingId(user.organization_user_id);
    setSelectedId(user.organization_user_id);
  }

  function dragEnd() { setDraggingId(null); setOverSlot(null); }

  function panStart(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "mouse" || event.button !== 0
      || (event.target as HTMLElement).closest("button, input, article, .pa-searchable-select")) return;
    const viewport = event.currentTarget;
    panRef.current = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.setPointerCapture(event.pointerId);
    setPanning(true);
    event.preventDefault();
  }

  function fit() {
    if (!layout) return;
    setZoom(Math.max(0.35, Math.min(1, (size.width - 32) / layout.width, (size.height - 24) / layout.height)));
    setFocusId(data?.root_organization_user_id ?? null);
  }

  if (reportingQuery.isPending) return <div className="reporting-loading" role="status"><Loader2 className="spin" size={22} /> Loading staff reporting...</div>;
  if (reportingQuery.error && !data) return <div className="reporting-load-error" role="alert">
    <strong>Staff reporting could not be loaded</strong><p>{reportingQuery.error.message}</p>
    <button className="pa-secondary-button" onClick={() => reportingQuery.refetch()}><RefreshCw size={16} /> Retry</button>
  </div>;
  if (!data || !layout) return null;

  return <div className="pa-reporting-page"><header className="pa-reporting-title"><h2>Staff reporting</h2><p>{data.can_manage ? "Arrange staff under their reporting managers. Changes save automatically." : "View staff reporting. Tech Admin access is required to make changes."}</p></header>
    <div className="reporting-page-actions">
      <span className={`reporting-save-state${change.isPending ? " pending" : change.error ? " failed" : ""}`} role="status">
        {change.isPending ? <Loader2 className="spin" size={15} /> : data.can_manage && !change.error ? <Check size={15} /> : null}
        {change.isPending ? "Saving..." : change.error ? "Not saved" : data.can_manage ? "Saved" : "View only"}
      </span>
      <button type="button" className="reporting-icon-button" title={undo ? `Undo change for ${undo.name}` : "Undo last change"}
        aria-label="Undo last reporting change" disabled={!editable || !undo}
        onClick={() => undo && change.mutate({ userId: undo.userId, managerId: undo.managerId, undo: true })}><Undo2 size={18} /></button>
      <button type="button" className="reporting-icon-button" title="Refresh reporting" aria-label="Refresh reporting"
        disabled={change.isPending || reportingQuery.isFetching} onClick={() => { change.reset(); void reportingQuery.refetch(); }}>
        <RefreshCw size={18} className={reportingQuery.isFetching ? "spin" : ""} /></button>
      <Link to={`/users?organization_id=${encodeURIComponent(organizationId)}`} className="pa-secondary-button"><Users size={16} /> Manage staff</Link>
    </div>
    {change.error ? <div className="pa-form-error reporting-error" role="alert">
      {change.error instanceof ApiError ? change.error.message : "Reporting could not be saved. Try again."}
      <button className="reporting-icon-button" title="Dismiss error" aria-label="Dismiss reporting error" onClick={() => change.reset()}><X size={16} /></button>
    </div> : null}
    <div className="pa-reporting-sr-only" aria-live="polite" role="status">{announcement}</div>

    <div className="staff-reporting-workspace">
      <aside className="reporting-staff-panel" aria-label="Staff outside the reporting tree">
        <header className="reporting-panel-header"><h2>Outside tree</h2><span className="reporting-count">{outside.length}</span></header>
        <label className="reporting-search"><Search size={17} aria-hidden="true" />
          <input aria-label="Search staff outside tree" placeholder="Search staff..." value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="reporting-staff-list" role="list">
          {visibleOutside.map((user) => <article key={user.organization_user_id}
            className={`reporting-staff-row${selectedId === user.organization_user_id ? " selected" : ""}`}
            role="listitem" draggable={editable} data-staff-id={user.organization_user_id}
            onDragStart={(event) => dragStart(event, user)} onDragEnd={dragEnd}>
            <button type="button" className="reporting-staff-select" disabled={!editable}
              aria-label={`Select ${displayName(user)}`} aria-pressed={selectedId === user.organization_user_id}
              onClick={() => setSelectedId(selectedId === user.organization_user_id ? null : user.organization_user_id)}>
              <Avatar user={user} /><span className="reporting-person-copy"><strong>{displayName(user)}</strong><small>{user.role?.name ?? "Staff"}</small></span>
              {data.can_manage ? <GripVertical size={17} className="reporting-grip" aria-hidden="true" /> : null}
            </button>
          </article>)}
          {!visibleOutside.length ? <div className="reporting-list-empty"><Users size={22} />
            <span>{outside.length ? "No matching staff" : "All staff are in the tree"}</span></div> : null}
        </div>
      </aside>

      <section className="reporting-tree-panel" aria-label="Reporting structure">
        <header className="reporting-tree-toolbar">
          <div className="reporting-tree-heading"><GitFork size={18} /><h2>Reporting tree</h2><span className="reporting-count">{layout.connected.size}</span></div>
          <div className="reporting-zoom-controls">
            <button className="reporting-icon-button" title="Zoom out" aria-label="Zoom out" disabled={zoom <= 0.35}
              onClick={() => setZoom((value) => Math.max(0.35, Math.round((value - 0.1) * 100) / 100))}><Minus size={17} /></button>
            <output aria-label="Tree zoom">{Math.round(zoom * 100)}%</output>
            <button className="reporting-icon-button" title="Zoom in" aria-label="Zoom in" disabled={zoom >= 1.5}
              onClick={() => setZoom((value) => Math.min(1.5, Math.round((value + 0.1) * 100) / 100))}><Plus size={17} /></button>
            <button className="reporting-icon-button" title="Fit tree" aria-label="Fit reporting tree" onClick={fit}><Maximize2 size={17} /></button>
          </div>
        </header>
        {selected ? <div className="reporting-selection-bar"><Avatar user={selected} /><strong>{displayName(selected)}</strong>
          <button className="reporting-icon-button" title="Cancel selection" aria-label="Cancel staff selection" onClick={() => setSelectedId(null)}><X size={16} /></button></div> : null}
        <div className={`reporting-viewport${panning ? " panning" : ""}`} ref={viewportRef} tabIndex={0}
          aria-label="Scrollable reporting tree" onPointerDown={panStart}
          onPointerMove={(event) => {
            const pan = panRef.current;
            if (pan) { event.currentTarget.scrollLeft = pan.left - (event.clientX - pan.x); event.currentTarget.scrollTop = pan.top - (event.clientY - pan.y); }
          }} onPointerUp={() => { panRef.current = null; setPanning(false); }} onPointerCancel={() => { panRef.current = null; setPanning(false); }}>
          {data.root_organization_user_id ? <div className="reporting-stage" style={{ width: layout.width * zoom, height: layout.height * zoom }}>
            <div className="reporting-graph" style={{ width: layout.width, height: layout.height, transform: `scale(${zoom})` }}>
              <svg className="reporting-connectors" width={layout.width} height={layout.height} aria-hidden="true">
                {layout.paths.map((path, index) => <path key={index} d={path} />)}
              </svg>
              {layout.nodes.map((node) => {
                const user = node.user;
                const parent = node.parentId ? usersById.get(node.parentId) : null;
                if (!user) {
                  const slot = { id: node.id, parentId: node.parentId! };
                  const options = outside.map((candidate) => ({ value: candidate.organization_user_id, label: displayName(candidate),
                      description: candidate.role?.name ?? "Staff", monogram: selectOptionMonogram(displayName(candidate)) }));
                  const validSelection = selected && canAssign(data.users, data.root_organization_user_id, selected.organization_user_id, slot.parentId);
                  const saving = change.isPending && change.variables?.slotId === slot.id;
                  return <article key={node.id} style={{ left: node.x, top: node.y }}
                    className={`reporting-slot${overSlot === slot.id ? " drag-over" : ""}`}
                    aria-label={`New reportee for ${parent ? displayName(parent) : "manager"}`} data-slot-id={slot.id}
                    onDragOver={(event) => {
                      if (!editable || !draggingId || !canAssign(data.users, data.root_organization_user_id, draggingId, slot.parentId)) return;
                      event.preventDefault(); event.dataTransfer.dropEffect = "move"; setOverSlot(slot.id);
                    }} onDragLeave={(event) => { if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setOverSlot(null); }}
                    onDrop={(event) => { event.preventDefault(); const id = event.dataTransfer.getData(TRANSFER_TYPE); setOverSlot(null); setDraggingId(null); if (id) assign(id, slot); }}>
                    <button className="reporting-icon-button reporting-slot-close" title="Cancel child slot" aria-label={`Cancel new reportee for ${parent ? displayName(parent) : "manager"}`}
                      disabled={!editable} onClick={() => setSlots((current) => current.filter((value) => value.id !== slot.id))}><X size={15} /></button>
                    {saving ? <Loader2 size={23} className="spin" /> : <UserPlus size={23} />}
                    <strong>New reportee</strong>
                    {validSelection ? <button className="reporting-assign-selected" disabled={!editable}
                      aria-label={`Assign ${displayName(selected)} to ${parent ? displayName(parent) : "manager"}`} onClick={() => assign(selected.organization_user_id, slot)}>
                      <Plus size={15} /> <span>{displayName(selected)}</span></button>
                      : <SearchableSelect value="" options={options} onChange={(id) => assign(id, slot)} placeholder="Choose staff"
                        label={`Choose reportee for ${parent ? displayName(parent) : "manager"}`} searchPlaceholder="Search staff..."
                        emptyMessage="No available staff" disabled={!editable} />}
                  </article>;
                }
                const owner = user.organization_user_id === data.root_organization_user_id;
                const directCount = children.get(user.organization_user_id)?.length ?? 0;
                const isCollapsed = collapsed.has(user.organization_user_id);
                return <article key={node.id} className={`reporting-person-node${owner ? " owner-node" : ""}${selectedId === node.id ? " selected" : ""}`}
                  style={{ left: node.x, top: node.y }} aria-label={`${displayName(user)}, ${owner ? "Owner" : user.role?.name ?? "Staff"}`}
                  draggable={editable && !owner} data-reporting-user-id={node.id} onDragStart={(event) => dragStart(event, user)} onDragEnd={dragEnd}>
                  <div className="reporting-node-body"><Avatar user={user} owner={owner} /><div className="reporting-person-copy">
                    <strong title={displayName(user)}>{displayName(user)}</strong><small>{owner ? <><Crown size={13} /> Owner</> : user.role?.name ?? "Staff"}</small>
                    {parent ? <span className="pa-reporting-sr-only">Reports to {displayName(parent)}</span> : null}
                  </div></div>
                  <footer className="reporting-node-actions">
                    {directCount ? <button className="reporting-reportee-count" title={`${isCollapsed ? "Expand" : "Collapse"} ${displayName(user)}'s reportees`}
                      aria-label={`${isCollapsed ? "Expand" : "Collapse"} reportees of ${displayName(user)}`} aria-expanded={!isCollapsed}
                      onClick={() => setCollapsed((current) => { const next = new Set(current); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next; })}>
                      {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />} {directCount}</button>
                      : <span className="reporting-no-reportees">0 reportees</span>}
                    {data.can_manage ? <div className="reporting-node-buttons">
                      {!owner ? <><button className="reporting-icon-button" title="Move branch" aria-label={`Move ${displayName(user)}`} disabled={!editable}
                        onClick={() => { setSelectedId(node.id); setAnnouncement(`${displayName(user)} selected for moving.`); }}><ArrowRightLeft size={15} /></button>
                        <button className="reporting-icon-button" title="Remove reporting link" aria-label={`Remove ${displayName(user)} from tree`} disabled={!editable}
                          onClick={() => setRemoveId(node.id)}><UserMinus size={15} /></button></> : null}
                      <button className="reporting-icon-button add-child" title="Add child" aria-label={`Add child to ${displayName(user)}`} disabled={!editable}
                        onClick={() => addChild(node.id)}><Plus size={18} /></button>
                    </div> : null}
                  </footer>
                </article>;
              })}
            </div>
          </div> : <div className="reporting-no-owner"><Crown size={28} /><strong>No active organization owner</strong><Link to={`/users?organization_id=${encodeURIComponent(organizationId)}`}>Manage staff</Link></div>}
        </div>
      </section>
    </div>

    {removal ? <div className="pa-reporting-backdrop" onClick={() => { if (!change.isPending) setRemoveId(null); }}>
      <section ref={removeDialogRef} className="reporting-remove-dialog" role="dialog" aria-modal="true" aria-labelledby="reporting-remove-title" onClick={(event) => event.stopPropagation()}>
        <h2 id="reporting-remove-title">Remove reporting link?</h2>
        <p>{displayName(removal)}{branchIds(data.users, removal.organization_user_id).size > 1 ? " and their reportees" : ""} will move outside the owner's tree. Links within the branch will be kept.</p>
        {change.error ? <div className="pa-form-error" role="alert">{change.error.message}</div> : null}
        <div className="reporting-dialog-actions"><button className="pa-secondary-button" disabled={change.isPending} onClick={() => setRemoveId(null)}>Cancel</button>
          <button className="reporting-remove-confirm" autoFocus disabled={change.isPending}
            onClick={() => change.mutate({ userId: removal.organization_user_id, managerId: null })}>
            {change.isPending ? <Loader2 className="spin" size={16} /> : <UserMinus size={16} />} Remove link</button></div>
      </section>
    </div> : null}
  </div>;
}

